import { useState, useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Sparkles, Send, Lightbulb, AlertTriangle, MessageSquare, Loader2 } from "lucide-react";
import { StatusBadge } from "./shared";

const SUGGESTIONS = [
  "Which branch has spent the most on Cosmeceuticals?",
  "Show me all delayed orders and their vendors",
  "Summarize savings generated across Sarita Vihar",
  "Which vendor has the worst on-time delivery?",
];

function renderMarkdown(text) {
  return text.split("\n").map((line, i) => {
    if (line.startsWith("### ")) return <h4 key={i} className="font-heading font-bold text-navy mt-3 mb-1">{line.replace("### ", "")}</h4>;
    if (line.startsWith("- ") || line.startsWith("* ")) return <li key={i} className="ml-4 list-disc text-sm text-slate-700">{line.replace(/^[-*]\s/, "")}</li>;
    if (!line.trim()) return <div key={i} className="h-1" />;
    return <p key={i} className="text-sm text-slate-700">{line}</p>;
  });
}

export default function AIPanel() {
  const { aiOpen, setAiOpen, aiInitialQuery } = useApp();
  const [tab, setTab] = useState("ask");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState("");
  const [risks, setRisks] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (aiOpen && aiInitialQuery) { setTab("ask"); ask(aiInitialQuery); }
    // eslint-disable-next-line
  }, [aiOpen, aiInitialQuery]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  const ask = async (text) => {
    const query = text || input;
    if (!query.trim()) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: query }]);
    setLoading(true);
    try {
      const res = await api.post("/ai/query", { query });
      setMessages((m) => [...m, { role: "ai", text: res.data.answer }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "Sorry, the AI service is unavailable right now." }]);
    } finally { setLoading(false); }
  };

  const loadInsights = async () => {
    if (insights || loading) return;
    setLoading(true);
    try { const res = await api.get("/ai/insights"); setInsights(res.data.answer); }
    catch { setInsights("AI service unavailable."); }
    finally { setLoading(false); }
  };

  const loadRisks = async () => {
    setLoading(true);
    try { const res = await api.get("/ai/delay-risk"); setRisks(res.data); }
    catch { setRisks([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!aiOpen) return;
    if (tab === "insights") loadInsights();
    if (tab === "risk") loadRisks();
    // eslint-disable-next-line
  }, [tab, aiOpen]);

  const TABS = [
    { id: "ask", label: "Ask", icon: MessageSquare },
    { id: "insights", label: "Insights", icon: Lightbulb },
    { id: "risk", label: "Delay Risk", icon: AlertTriangle },
  ];

  return (
    <Sheet open={aiOpen} onOpenChange={setAiOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-gradient-to-b from-teal-50/60 to-white">
        <SheetHeader className="px-5 py-4 border-b border-border bg-white/80 backdrop-blur">
          <SheetTitle className="flex items-center gap-2 font-heading text-navy">
            <div className="w-8 h-8 rounded-lg bg-brand-teal flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
            Procurement Intelligence
          </SheetTitle>
          <SheetDescription>Ask questions, view AI insights, or check delay risks across your procurement data.</SheetDescription>
        </SheetHeader>

        <div className="flex gap-1 px-4 py-2 bg-white/60 border-b border-border">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`ai-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                tab === t.id ? "bg-brand-teal text-white" : "text-brand-slate hover:bg-slate-100"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "ask" && (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-brand-slate">Ask anything about your procurement data:</p>
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid="ai-suggestion" onClick={() => ask(s)}
                      className="block w-full text-left text-sm bg-white border border-border rounded-lg px-3 py-2 hover:border-brand-teal transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 ${m.role === "user" ? "bg-navy text-white" : "bg-white border border-border"}`}>
                    {m.role === "ai" ? <div className="space-y-0.5">{renderMarkdown(m.text)}</div> : <p className="text-sm">{m.text}</p>}
                  </div>
                </div>
              ))}
              {loading && <div className="flex items-center gap-2 text-brand-slate text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</div>}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-3 border-t border-border bg-white flex gap-2">
              <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
                placeholder="Type your question…"
                className="flex-1 h-10 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/40" />
              <Button data-testid="ai-send" type="submit" disabled={loading} className="h-10 bg-brand-teal hover:bg-brand-tealdark px-3">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </>
        )}

        {tab === "insights" && (
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? <div className="flex items-center gap-2 text-brand-slate text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Generating executive summary…</div>
              : <div className="bg-white border border-border rounded-xl p-4 space-y-0.5" data-testid="ai-insights">{renderMarkdown(insights)}</div>}
          </div>
        )}

        {tab === "risk" && (
          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="ai-risk-list">
            {loading ? <div className="flex items-center gap-2 text-brand-slate text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</div>
              : risks.length === 0 ? <p className="text-sm text-brand-slate">No open orders to assess.</p>
              : risks.map((r, i) => (
                <div key={i} className="bg-white border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-navy text-sm">{r.item_name}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.risk === "High" ? "bg-rose-100 text-rose-700" : r.risk === "Medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.risk} risk</span>
                  </div>
                  <div className="text-xs text-brand-slate mt-1">{r.vendor_name} · {r.branch} · avg delay {r.avg_vendor_delay}d</div>
                  <div className="mt-1"><StatusBadge value={r.order_status} /></div>
                </div>
              ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
